import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Grid, Box, Typography, Chip, Divider,
  CircularProgress, Backdrop,
  Accordion, AccordionSummary, AccordionDetails,
  Tooltip, IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Check, Info, Droplet, Share2, ChartNoAxesCombined, Plus, Minus } from "lucide-react";
import { useTheme, alpha } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "../../../../language";
import { API_URL } from "../../../../constants";
import { useAuth } from "../../../../provider";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../../components/Dialog/DiaCompMedium";
import TextInfoComp from "../ReuseComps/TextInfoComp";
import MarkerTrendChart from "../ReuseComps/MarkerTrendChart";
import AMHChart from "./AMHChart";
import { pressable, pressSx } from "../../../../components/Pressable/pressable";

const SUBSYSTEM_KEY = {
  "Ovarian Stock Reserver": "subsystemAmh",
};

const CAROUSEL_VISIBLE = 2;

function statusColor(theme, status) {
  if (!status) return theme.palette.grey[400];
  if (status.toUpperCase() === "NORMAL") return theme.palette.traffic.green;
  return theme.palette.traffic.red;
}

function parseDateParts(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { month: "—", day: "—", year: "—" };
  return {
    month: d.toLocaleString("en", { month: "short" }).toUpperCase(),
    day:   String(d.getDate()).padStart(2, "0"),
    year:  String(d.getFullYear()),
  };
}

function DateCarousel({ items, selectedId, onSelect, initialIndex = 0 }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const visibleCount = isMobile ? 2 : CAROUSEL_VISIBLE;
  const [startIndex, setStartIndex] = useState(initialIndex);
  useEffect(() => { setStartIndex(initialIndex); }, [initialIndex]);
  const safeItems = items ?? [];
  const canPrev = startIndex > 0;
  const canNext = startIndex + visibleCount < safeItems.length;
  const visible = safeItems.slice(startIndex, startIndex + visibleCount);
  return (
    <Box sx={{
      display: "flex", alignItems: "center",
      borderRadius: 4, px: 1, py: 1.5,
      bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: theme.palette.mode === "dark"
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
    }}>
      <IconButton size="small" onClick={() => setStartIndex(i => i - 1)} disabled={!canPrev}>
        <ChevronLeftIcon />
      </IconButton>
      <Box sx={{ display: "flex", gap: 1.5, mx: 0.5 }}>
        {visible.map((item) => {
          const isProposal = Boolean(item.proposal);
          const isPending  = Boolean(item.pending);
          const { month, day, year } = parseDateParts(item.visit_date);
          const selected = !isProposal && item.id === selectedId;
          return (
            <Box
              key={isProposal ? `proposal-${item.visit_date}` : item.id}
              onClick={isProposal ? undefined : () => onSelect(item.id)}
              sx={{
                cursor: isProposal ? "default" : "pointer",
                opacity: isPending ? 1 : isProposal ? 0.45 : 1,
                width: 96, py: selected ? 1.75 : 1.25, px: 0.5, mx: selected ? 1.5 : 0,
                borderRadius: 3,
                border: selected ? "2px solid" : isProposal ? "1.5px dashed" : "2px solid",
                borderColor: selected
                  ? "primary.main"
                  : isProposal
                    ? (theme.palette.mode === "light" ? "grey.400" : "greydark.C400")
                    : (theme.palette.mode === "light" ? "grey.C900" : "greydark.C400"),
                display: "flex", flexDirection: "column", alignItems: "center",
                bgcolor: selected
                  ? `${theme.palette.primary.main}18`
                  : isProposal
                    ? (theme.palette.mode === "light" ? "grey.C100" : "greydark.C900")
                    : "transparent",
                transform: selected ? "scale(1.1)" : "scale(1)",
                boxShadow: selected
                  ? `0 4px 18px ${theme.palette.primary.main}40`
                  : "none",
                zIndex: selected ? 1 : 0,
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
            >
              {item.visitLabel && (
                <Typography variant="caption" sx={{
                  fontSize: selected ? "0.7rem" : "0.65rem", fontWeight: 600,
                  color: selected ? "primary.main" : "text.disabled",
                  letterSpacing: 0.3, textAlign: "center",
                }}>
                  {item.visitLabel}
                </Typography>
              )}
              {item.visitLabel && <Divider flexItem sx={{ width: "100%", my: 0.5 }} />}
              <Typography variant="caption" fontWeight={700} sx={{
                color: selected ? "primary.main" : "text.secondary",
                letterSpacing: 0.5, fontSize: selected ? "0.8rem" : "0.72rem",
              }}>
                {month}
              </Typography>
              <Typography variant="h5" fontWeight={800} sx={{
                lineHeight: 1.15, my: 0.25,
                color: isProposal ? "text.disabled" : selected ? "primary.main" : "text.primary",
                fontSize: selected ? "1.75rem" : "1.35rem",
              }}>
                {day}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: selected ? "0.8rem" : "0.72rem" }}>
                {year}
              </Typography>
              {isPending && (
                <Chip
                  label={t("bt_in_progress")}
                  size="small"
                  sx={{
                    mt: 0.75,
                    height: 16,
                    fontSize: "0.52rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    bgcolor: "transparent",
                    color: theme.palette.traffic.orange,
                    border: `1px solid ${theme.palette.traffic.orange}66`,
                    borderRadius: "4px",
                    "& .MuiChip-label": { px: "5px" },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <IconButton size="small" onClick={() => setStartIndex(i => i + 1)} disabled={!canNext}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
}

function GaugeBar({ value, low, high }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;
  const val = Number(value);
  const lo  = low  != null ? Number(low)  : null;
  const hi  = high != null ? Number(high) : null;
  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5;
    dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5);
    dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5);
    dMax = Math.max(hi * 1.5, val * 1.2);
  }
  const pad = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad);
  dMax = Math.max(dMax, val + pad);
  const span    = dMax - dMin;
  const clamp   = (v) => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct  = lo != null ? clamp(lo) : 0;
  const highPct = hi != null ? clamp(hi) : 100;
  const valPct  = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);
  return (
    <Box sx={{ position: "relative", width: "100%", pb: 1 }}>
      <Box sx={{ position: "relative", width: "100%", height: 5, bgcolor: "action.disabledBackground", borderRadius: 0.5 }}>
        <Box sx={{ position: "absolute", left: `${lowPct}%`, width: `${highPct - lowPct}%`, height: "100%", bgcolor: theme.palette.traffic.green, opacity: 0.4 }} />
        <Box sx={{ position: "absolute", top: "50%", left: `${valPct}%`, transform: "translate(-50%, -50%)", width: 4, height: 14, bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${lowPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {lo.toFixed(2)}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${highPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {hi.toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

export default function OvarianDetailsFemale() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { state } = useAuth();
  const gender = state.user?.gender;

  const [visitOptions, setVisitOptions]   = useState([]);
  const [selectedId, setSelectedId]       = useState(null);
  const [reportError, setReportError]     = useState(false);
  const [datesLoading, setDatesLoading]   = useState(true);
  const [chartOpen, setChartOpen]         = useState(false);
  const [chartMarker, setChartMarker]     = useState(null);
  const [collapsedSubsystems, setCollapsedSubsystems] = useState(() => new Set());
  const [cycleDay, setCycleDay]           = useState(null);
  const [cyclePhase, setCyclePhase]       = useState(null);
  const [proposalDates, setProposalDates] = useState([]);
  const [pendingVisits, setPendingVisits] = useState([]);
  const [planOption, setPlanOption]       = useState(null);
  const [infoOpen, setInfoOpen]           = useState(false);
  const [infoMarker, setInfoMarker]       = useState(null);
  const [shareOpen, setShareOpen]         = useState(false);
  const [shareInfoOpen, setShareInfoOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/customer-profile/me/`)
      .then(({ data }) => setPlanOption(data.plan_option ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!visitOptions.length || !planOption) return;
    const plan           = Number(planOption);
    const intervalMonths = [2, 4].includes(plan) ? 6 : 3;
    const count          = [2, 4].includes(plan) ? 2 : 4;
    const lastDate       = [...visitOptions, ...pendingVisits]
      .map((v) => v.visit_date).filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0] ?? visitOptions[0].visit_date;
    const bookedDates = new Set([
      ...visitOptions.map((v) => v.visit_date),
      ...pendingVisits.map((v) => v.visit_date),
    ]);
    const remaining = Math.max(0, count - pendingVisits.length);
    const proposals = [];
    for (let i = 1; proposals.length < remaining && i <= count * 3; i++) {
      const d = new Date(lastDate + "T00:00:00");
      d.setMonth(d.getMonth() + intervalMonths * i);
      const dateStr = d.toISOString().split("T")[0];
      if (!bookedDates.has(dateStr)) proposals.push({ proposal: true, visit_date: dateStr });
    }
    setProposalDates(proposals);
  }, [visitOptions, pendingVisits, planOption]);

  useEffect(() => {
    const visitDate = visitOptions.find(v => v.id === selectedId)?.visit_date;
    if (!visitDate || gender !== "F") return;
    axios.get(`${API_URL}/api/cycles/by-date/?target_date=${visitDate}`)
      .then(res => {
        setCycleDay(res.data.cycle_day ?? null);
        setCyclePhase(res.data.cycle_phase ?? null);
      })
      .catch(() => { setCycleDay(null); setCyclePhase(null); });
  }, [selectedId, visitOptions, gender]);

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/all-lab-visits/results/`);
        const raw = res.data;
        const labVisits = Array.isArray(raw) ? raw : (raw.lab_visits || []);
        const completed = labVisits.filter((v) => Number(v.state) === 6).sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
        const pending   = labVisits.filter((v) => Number(v.state) > 1 && Number(v.state) < 6 && v.visit_date).sort((a, b) => new Date(a.visit_date) - new Date(b.visit_date));
        setVisitOptions(completed);
        setPendingVisits(pending);
        if (completed.length > 0) setSelectedId((prev) => prev ?? completed[0].id);
      } catch (err) {
        console.error("Failed to fetch lab visits", err);
        setReportError("fetch");
      } finally {
        setDatesLoading(false);
      }
    };
    fetchVisits();
  }, []);

  useEffect(() => {
    if (!selectedId || !visitOptions.length) return;
    const v = visitOptions.find((v) => v.id === selectedId);
    setReportError(v?.results?.length ? false : "empty");
  }, [selectedId, visitOptions]);

  const report = visitOptions.find((v) => v.id === selectedId) ?? null;

  const fertilityResults = useMemo(
    () => (report?.results ?? []).filter(r => r.marker_category === "Fertility (Female)"),
    [selectedId, visitOptions]
  );

  const groupedBySubsystem = useMemo(() => {
    const map = {};
    for (const m of fertilityResults) {
      const key = m.marker_healthsubsystem?.trim() ?? "__none__";
      if (!map[key]) map[key] = [];
      map[key].push(m);
    }
    Object.values(map).forEach(g => g.sort((a, b) => (a.marker_order ?? 0) - (b.marker_order ?? 0)));
    return map;
  }, [fertilityResults]);

  const amhResult = useMemo(() => {
    const m = fertilityResults.find(r => (r.marker_short ?? "").toLowerCase().includes("amh"));
    if (!m) return null;
    const parsed = parseFloat(m.value);
    if (isNaN(parsed)) return null;
    return {
      value:        parsed,
      unit:         m.unit,
      status:       (m.status ?? "").toUpperCase(),
      marker_short: m.marker_short,
      ref_low:      m.ref_low  ?? null,
      ref_high:     m.ref_high ?? null,
    };
  }, [fertilityResults]);

  if (datesLoading) {
    return (
      <Backdrop open sx={{ zIndex: t => t.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (visitOptions.length === 0) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography color="text.secondary">{t("bt_no_visits")}</Typography>
      </Box>
    );
  }

  const cycleCount     = [2, 4].includes(Number(planOption)) ? 2 : 4;
  const reversedVisits = [...visitOptions].reverse().map(v => ({
    ...v,
    visitLabel: t(`bt_labvisit_${v.visit_number}`),
  }));
  const pendingSorted = [...pendingVisits]
    .sort((a, b) => new Date(a.visit_date) - new Date(b.visit_date))
    .map((v) => ({
      ...v,
      proposal: true,
      pending: true,
      visitLabel: t(`bt_labvisit_${v.visit_number}`),
    }));
  const lastNum   = Number(visitOptions[0]?.visit_number ?? 0);
  const proposals = [...proposalDates]
    .sort((a, b) => new Date(a.visit_date) - new Date(b.visit_date))
    .map((p, idx) => ({
      ...p,
      visitLabel: t(`bt_labvisit_${(lastNum + pendingSorted.length + idx) % cycleCount + 1}`),
    }));
  const carouselItems = [...reversedVisits, ...pendingSorted, ...proposals];
  // Default to showing the most recent completed lab visits
  const initialIndex  = Math.max(0, reversedVisits.length - CAROUSEL_VISIBLE);

  const subsystemKeys = Object.keys(groupedBySubsystem).sort((a, b) => {
    if (a === "__none__") return 1;
    if (b === "__none__") return -1;
    return a.localeCompare(b);
  });

  const hasResults = report && !reportError;
  const isFemale   = gender === "F";

  return (
    <Grid container justifyContent="center" sx={{ mt: 1, mb: 10 }}>

      {/* ── Date Selector Carousel ─────────────────────────────────────────── */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 4 }}>
        <DateCarousel
          items={carouselItems}
          selectedId={selectedId}
          onSelect={setSelectedId}
          initialIndex={initialIndex}
        />
      </Grid>

      {/* ── Cycle phase + Share with doctor widgets ────────────────────────── */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
        <Box sx={{
          width: "100%", maxWidth: 800,
          p: 3, borderRadius: 4,
          bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
          backdropFilter: "blur(28px) saturate(180%)",
          WebkitBackdropFilter: "blur(28px) saturate(180%)",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
          boxShadow: theme.palette.mode === "dark"
            ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
            : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5,
        }}>

          {/* Cycle phase */}
          <Box sx={{
            minWidth: 0,
            display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75,
            p: 1.5, borderRadius: 3,
            border: "1px solid", borderColor: "divider",
            opacity: isFemale ? 1 : 0.45,
          }}>
            <Box sx={{ width: 30, height: 30, borderRadius: 2, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: theme.palette.mode === "light" ? "greydark.C900" : "grey.C50" }}>
              <Droplet size={15} color={theme.palette.mode === "light" ? theme.palette.grey.C50 : theme.palette.greydark.C900} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.015em", color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", display: "block", fontSize: "0.65rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t("bt_cycle_status")}
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2, color: "text.secondary", textTransform: "capitalize" }}>
                {isFemale ? (cyclePhase ?? "—") : "—"}
              </Typography>
              {isFemale && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6rem" }}>
                  Day {cycleDay ?? "—"} of 28
                </Typography>
              )}
            </Box>
          </Box>

          {/* Share with doctor */}
          <Box {...pressable(() => setShareOpen(true))} sx={{ ...pressSx,
            minWidth: 0,
            display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75,
            p: 1.5, borderRadius: 3,
            border: "1px solid", borderColor: "divider",
            cursor: "pointer",
            transition: "border-color 0.15s, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
            "&:hover": { borderColor: "primary.main" },
          }}>
            <Box sx={{ width: 30, height: 30, borderRadius: 2, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: theme.palette.mode === "light" ? "greydark.C900" : "grey.C50" }}>
              <Share2 size={15} color={theme.palette.mode === "light" ? theme.palette.grey.C50 : theme.palette.greydark.C900} />
            </Box>
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.015em", color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", fontSize: "0.65rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t("bt_share_with_doctor")}
                </Typography>
                <Tooltip title="Info" placement="top">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShareInfoOpen(true); }} sx={{ p: 0.2 }}>
                    <Info size={11} color={theme.palette.secondary.main} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2, color: "text.secondary" }}>
                {t("bt_share_action")}
              </Typography>
            </Box>
          </Box>

        </Box>
      </Grid>

      {/* ── Error / empty state ───────────────────────────────────────────── */}
      {reportError && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2 }}>
          <Box sx={{ width: "100%", maxWidth: 800, p: 2.5, borderRadius: 3, border: "0.5px solid", borderColor: "divider", textAlign: "center" }}>
            <Typography color="text.secondary">
              {reportError === "empty" ? t("bt_no_results_yet") : t("bt_results_error")}
            </Typography>
          </Box>
        </Grid>
      )}

      {/* ── Ovarian Reserve markers ───────────────────────────────────────── */}
      {hasResults && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 0.5, pt: 1, mb: 1.5 }}>
              <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", whiteSpace: "nowrap" }}>
                {t("amh_marker_overview_heading")}
              </Typography>
            </Box>
            {subsystemKeys.length === 0 ? (
              <Box sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", textAlign: "center" }}>
                <Typography variant="body2" fontWeight={700} color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {t("bt_not_ordered")}
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                  {t("bt_consider_addon")}
                </Typography>
              </Box>
            ) : (
              subsystemKeys.map((sk) => {
                const markers     = groupedBySubsystem[sk];
                const subLabelKey = SUBSYSTEM_KEY[sk];
                const subLabel    = sk === "__none__" ? null : (subLabelKey ? t(subLabelKey) : sk);
                const redCount    = markers.filter(m => m.status && m.status.toUpperCase() !== "NORMAL").length;
                const isOpen      = !collapsedSubsystems.has(sk);
                return (
                  <Box key={sk} sx={{ mb: 2 }}>
                    <Accordion
                      expanded={isOpen}
                      onChange={(e, expandedNow) => {
                        setCollapsedSubsystems((prev) => {
                          const next = new Set(prev);
                          if (expandedNow) next.delete(sk); else next.add(sk);
                          return next;
                        });
                      }}
                      disableGutters
                      elevation={0}
                      slotProps={{ transition: { timeout: 320 } }}
                      sx={{
                        borderRadius: "12px !important",
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                        backdropFilter: "blur(28px) saturate(180%)",
                        WebkitBackdropFilter: "blur(28px) saturate(180%)",
                        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.35)"}`,
                        boxShadow: theme.palette.mode === "dark"
                          ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
                          : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={isOpen
                          ? <Minus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />
                          : <Plus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />}
                        sx={{
                          pl: 1.25, pr: 2.5,
                          bgcolor: "transparent",
                          minHeight: 44,
                          "& .MuiAccordionSummary-content": { my: 0.5, display: "flex", alignItems: "center", gap: 1, pr: 2 },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {subLabel && !isOpen && (
                            <Typography variant="subtitle2" fontWeight={700}
                              sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                              {subLabel}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        {!isOpen && (redCount > 0 ? (
                          <Tooltip title={t("bt_tooltip_out_of_range")} placement="top">
                            <Chip
                              label={`${redCount}`}
                              size="small"
                              sx={{
                                bgcolor: "transparent",
                                color: theme.palette.traffic.red,
                                border: "1px solid", borderColor: theme.palette.traffic.red,
                                fontWeight: 500, fontSize: "0.8rem",
                                width: 22, height: 22,
                                borderRadius: "999px",
                                "& .MuiChip-label": { px: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t("bt_tooltip_all_in_range")} placement="top">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Check size={18} strokeWidth={2.5} color={theme.palette.traffic.green} />
                            </Box>
                          </Tooltip>
                        ))}
                      </AccordionSummary>

                      <AccordionDetails sx={{ p: 0 }}>
                        {markers.map((m, i) => {
                          const statusKey  = `bt_status_${(m.status || "").toLowerCase().replace(/\s+/g, "_")}`;
                          const statusText = t(statusKey) !== statusKey ? t(statusKey) : m.status;
                          return (
                            <Box key={m.id}>
                              {i > 0 && <Divider sx={{ mx: 2 }} />}
                              <Box sx={{ display: "flex", flexDirection: "column", px: 2, py: 0.75, gap: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: statusColor(theme, m.status), mt: "7px" }} />
                                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography variant="body1" fontWeight={500}>{t(`markername_${m.marker_short}`)}</Typography>
                                    <Typography variant="caption" color="text.secondary">{t(`markerdescr_${m.marker_short}`)}</Typography>
                                  </Box>
                                </Box>

                                {/* Value, gauge, status, actions */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                                  <Typography variant="number3" fontWeight={400} sx={{ minWidth: 80, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                    {m.value != null ? Number(m.value).toFixed(2) : "—"}{m.unit ? ` ${m.unit}` : ""}
                                  </Typography>
                                  <Box sx={{ minWidth: 140, ml: 2, display: "flex", alignItems: "center" }}>
                                    <GaugeBar value={m.value} low={m.ref_low} high={m.ref_high} />
                                  </Box>
                                  <Box sx={{ minWidth: 68, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                                    {m.status?.toUpperCase() === "NORMAL"
                                      ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                                      : <Typography variant="subtitle2" fontWeight={700} sx={{ color: statusColor(theme, m.status) }}>{statusText}</Typography>
                                    }
                                  </Box>
                                  <Tooltip title="Info" placement="top">
                                    <IconButton size="small" onClick={() => { setInfoMarker(m.marker_short); setInfoOpen(true); }} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                                      <Info size={18} color={theme.palette.secondary.main} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("bt_chart_history")} placement="top">
                                    <IconButton size="small" onClick={() => { setChartMarker(m.marker_short); setChartOpen(true); }} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                                      <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                );
              })
            )}
          </Box>
        </Grid>
      )}

      {/* ── AMH Chart ─────────────────────────────────────────────────────── */}
      {hasResults && amhResult && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2 }}>
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 0.5, pt: 1, mb: 1.5 }}>
              <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", whiteSpace: "nowrap" }}>
                {t("amh_panel_title")}
              </Typography>
            </Box>
            <AMHChart result={amhResult} />
          </Box>
        </Grid>
      )}

      {/* ── Chart history dialog ─────────────────────────────────────────── */}
      <CustomDialog
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        title={chartMarker ? `${t(`markername_${chartMarker}`)} — ${t("bt_chart_title")}` : ""}
        content={chartMarker && <MarkerTrendChart markerShort={chartMarker} visitOptions={visitOptions} theme={theme} t={t} />}
      />

      {/* ── Info dialog ───────────────────────────────────────────────────── */}
      <CustomDialogSmall
        open={infoOpen}
        onClose={() => { setInfoOpen(false); setInfoMarker(null); }}
        title="Information"
        content={infoMarker && (
          <TextInfoComp
            description={`description_${infoMarker}`}
            lowtext={`lowtext_${infoMarker}`}
            lowrec={`lowrec_${infoMarker}`}
            rangetext={`rangetext_${infoMarker}`}
            hightext={`hightext_${infoMarker}`}
            highrec={`highrec_${infoMarker}`}
          />
        )}
      />

      {/* ── Share info dialog ─────────────────────────────────────────────── */}
      <CustomDialogSmall
        open={shareInfoOpen}
        onClose={() => setShareInfoOpen(false)}
        title={t("bt_share_with_doctor")}
        content={
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="body1">{t("bt_share_info_text")}</Typography>
          </Box>
        }
      />

      {/* ── Share with doctor dialog ──────────────────────────────────────── */}
      <CustomDialogSmall
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={t("bt_share_with_doctor")}
        content={null}
      />

    </Grid>
  );
}
